import { useEffect, useRef, useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { withdrawalProcessAPI } from '../lib/api';
import { receiptUrl } from '../components/admin/BankTransferReceiptFields';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
    Landmark, Copy, Check, UploadCloud, Loader2, Paperclip, X, ShieldAlert, FileText, Clock, Search, CheckCircle2, XCircle, Eye, ScrollText, Target, UserCheck,
} from 'lucide-react';

const fmt = (a) => `${Number(a || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const MAX_BYTES = 12 * 1024 * 1024;
const STATUS = {
    pending: { cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30', Icon: Clock },
    in_review: { cls: 'bg-sky-500/15 text-sky-300 border-sky-500/30', Icon: Search },
    verified: { cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', Icon: CheckCircle2 },
    rejected: { cls: 'bg-red-500/15 text-red-300 border-red-500/30', Icon: XCircle },
};

const CopyButton = ({ text, label, testId }) => {
    const [done, setDone] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setDone(true); toast.success(`${label} copiado`);
            setTimeout(() => setDone(false), 1800);
        } catch { toast.error('No se pudo copiar'); }
    };
    return (
        <button type="button" onClick={copy} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#7CB1E5] hover:text-white transition-colors" data-testid={testId}>
            {done ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />} {done ? 'Copiado' : 'Copiar'}
        </button>
    );
};

const ModalityCard = ({ m, selected, onSelect }) => (
    <button type="button" onClick={() => onSelect(m)}
        className={`text-left rounded-2xl border p-5 transition-all ${selected ? 'bg-[#1973B8]/15 border-[#1973B8] shadow-[0_0_0_1px_#1973B8]' : 'bg-slate-900/70 border-slate-800 hover:border-slate-600'}`}
        data-testid={`modality-card-${m.id}`}>
        <p className="text-[11px] uppercase tracking-wider text-slate-500">{m.subtitle}</p>
        <p className="text-2xl font-bold text-white mt-1 tabular-nums">{fmt(m.amount)}</p>
        <p className="text-sm text-slate-300 mt-1">{m.title}</p>
        {selected && <p className="text-[11px] text-[#7CB1E5] font-semibold mt-3 flex items-center gap-1"><Check className="w-3 h-3" /> Modalidad seleccionada</p>}
    </button>
);

const TermsPanel = ({ m, accepted, onAccept }) => (
    <div className="rounded-2xl bg-slate-900/70 border border-amber-500/30 p-5 space-y-4" data-testid="terms-panel">
        <div className="flex items-center gap-2">
            <ScrollText className="w-4 h-4 text-amber-300" />
            <p className="text-white font-semibold text-sm">Verifica las condiciones antes de efectuar cualquier pago</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            {[
                { Icon: ScrollText, label: 'Fundamento contractual', text: m.legal_basis, id: 'legal' },
                { Icon: UserCheck, label: 'Destinatario', text: m.recipient, id: 'recipient' },
                { Icon: Target, label: 'Finalidad del importe', text: m.purpose, id: 'purpose' },
            ].map((b) => (
                <div key={b.id} className="rounded-xl bg-slate-950 border border-slate-800 p-3" data-testid={`terms-${b.id}`}>
                    <p className="text-[10.5px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><b.Icon className="w-3 h-3" /> {b.label}</p>
                    <p className="text-slate-300 text-xs mt-1.5 leading-relaxed">{b.text}</p>
                </div>
            ))}
        </div>
        <label className="flex items-start gap-3 cursor-pointer rounded-xl bg-slate-950 border border-slate-800 p-3">
            <Checkbox checked={accepted} onCheckedChange={(v) => onAccept(!!v)} className="mt-0.5 border-slate-600 data-[state=checked]:bg-[#1973B8]" data-testid="terms-accept-checkbox" />
            <span className="text-xs text-slate-300 leading-relaxed">
                He leído y verificado el fundamento contractual, el destinatario y la finalidad del importe de <strong className="text-white">{fmt(m.amount)}</strong> ({m.subtitle}). Entiendo que adjuntar un justificante <strong className="text-white">no activa ni garantiza un retiro automático</strong>.
            </span>
        </label>
    </div>
);

const AgentData = ({ agent }) => {
    const all = `Agente: ${agent.name}\nBanco: ${agent.bank}\nIBAN: ${agent.iban}`;
    return (
        <div className="rounded-2xl bg-slate-900/70 border border-slate-800 p-5 space-y-3" data-testid="agent-data">
            <div className="flex items-center justify-between gap-2">
                <p className="text-white font-semibold text-sm tracking-wide">DATOS DEL AGENTE AUTORIZADO</p>
                <Button size="sm" variant="outline" onClick={async () => { try { await navigator.clipboard.writeText(all); toast.success('Datos bancarios copiados'); } catch { toast.error('No se pudo copiar'); } }}
                    className="h-8 border-[#1973B8]/50 text-[#7CB1E5] hover:bg-[#1973B8]/15" data-testid="copy-all-bank-data">
                    <Copy className="w-3.5 h-3.5 mr-1.5" /> Copiar datos bancarios
                </Button>
            </div>
            <div className="divide-y divide-slate-800/70 text-sm">
                {[['Agente', agent.name, 'agent-name'], ['Banco', agent.bank, 'agent-bank'], ['IBAN', agent.iban, 'agent-iban']].map(([k, v, id]) => (
                    <div key={k} className="flex items-center justify-between py-2.5 gap-3">
                        <span className="text-slate-500 text-xs uppercase tracking-wider w-16">{k}</span>
                        <span className={`text-white flex-1 ${k === 'IBAN' ? 'font-mono tracking-wide' : 'font-medium'}`} data-testid={id}>{v}</span>
                        <CopyButton text={v} label={k} testId={`copy-${id}`} />
                    </div>
                ))}
            </div>
        </div>
    );
};

const RequestItem = ({ r }) => {
    const st = STATUS[r.status] || STATUS.pending;
    return (
        <div className="px-4 py-3.5 flex flex-wrap items-start gap-3" data-testid={`wp-request-${r.id}`}>
            <FileText className="w-5 h-5 text-slate-500 mt-0.5" />
            <div className="flex-1 min-w-[200px]">
                <p className="text-white text-sm font-semibold">{fmt(r.amount)} · {r.modality_subtitle}</p>
                <p className="text-slate-500 text-[11px]">{r.filename} · Enviado {fmtDate(r.created_at)}</p>
                {r.reason && r.status === 'rejected' && <p className="text-red-300 text-xs mt-1 bg-red-500/10 border border-red-500/25 rounded-lg px-2.5 py-1.5" data-testid={`wp-reason-${r.id}`}>Motivo: {r.reason}</p>}
                <div className="flex flex-wrap gap-1.5 mt-2">
                    {(r.history || []).map((h, i) => (
                        <span key={i} className="text-[10px] text-slate-500 bg-slate-950 border border-slate-800 rounded-md px-1.5 py-0.5">{STATUS_LABEL(h.status)} · {fmtDate(h.at)}</span>
                    ))}
                </div>
            </div>
            <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[11px] font-semibold ${st.cls}`} data-testid={`wp-status-${r.id}`}>
                    <st.Icon className="w-3 h-3" /> {STATUS_LABEL(r.status)}
                </span>
                <a href={receiptUrl(r.file_id)} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-white" title="Ver justificante" data-testid={`wp-view-${r.id}`}><Eye className="w-4 h-4" /></a>
            </div>
        </div>
    );
};
const LABELS = { pending: 'Pendiente de revisión', in_review: 'En revisión', verified: 'Verificado', rejected: 'Rechazado' };
const STATUS_LABEL = (s) => LABELS[s] || s;

const TransferToMyBankPage = () => {
    const [cfg, setCfg] = useState(null);
    const [requests, setRequests] = useState([]);
    const [selected, setSelected] = useState(null);
    const [accepted, setAccepted] = useState(false);
    const [file, setFile] = useState(null);
    const [note, setNote] = useState('');
    const [sending, setSending] = useState(false);
    const fileRef = useRef(null);

    const loadMine = () => withdrawalProcessAPI.mine().then((r) => setRequests(r.data.requests || [])).catch(() => {});
    useEffect(() => {
        withdrawalProcessAPI.config().then((r) => setCfg(r.data)).catch(() => toast.error('No se pudo cargar la configuración'));
        loadMine();
    }, []);

    const pickFile = (f) => {
        if (!f) { setFile(null); return; }
        if (f.size > MAX_BYTES) { toast.error('Archivo demasiado grande (máx 12 MB)'); return; }
        setFile(f);
    };
    const reset = () => { setFile(null); setNote(''); setAccepted(false); setSelected(null); if (fileRef.current) fileRef.current.value = ''; };

    const submit = async () => {
        if (!selected || !accepted || !file) return;
        setSending(true);
        try {
            const fd = new FormData();
            fd.append('modality', selected.id);
            fd.append('accepted_terms', 'true');
            fd.append('note', note);
            fd.append('file', file);
            await withdrawalProcessAPI.submit(fd);
            toast.success('Justificante enviado. Administración revisará la operación.');
            reset(); loadMine();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al enviar el justificante');
        } finally { setSending(false); }
    };

    return (
        <Layout>
            <div className="max-w-5xl mx-auto space-y-6" data-testid="transfer-to-my-bank-page">
                <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-[#1973B8]/15 border border-[#1973B8]/30">
                        <Landmark className="w-6 h-6 text-[#7CB1E5]" />
                    </div>
                    <div>
                        <p className="text-[10.5px] uppercase tracking-[0.14em] text-[#7CB1E5] font-bold">Transferir a mi banco</p>
                        <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight" data-testid="wp-title">{cfg?.title || 'ABONAR PROCESO DE RETIRO'}</h1>
                        <p className="text-sm text-slate-400 font-light">Selecciona una modalidad, verifica las condiciones y adjunta el justificante de tu transferencia</p>
                    </div>
                </div>

                <div className="flex items-start gap-2 rounded-xl bg-amber-500/10 border border-amber-500/25 px-4 py-3 text-xs text-amber-200" data-testid="wp-disclaimer">
                    <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <p>{cfg?.disclaimer || 'La presentación de un justificante no activa ni garantiza ningún retiro de forma automática.'}</p>
                </div>

                {!cfg ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#7CB1E5]" /></div> : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {cfg.modalities.map((m) => <ModalityCard key={m.id} m={m} selected={selected?.id === m.id} onSelect={(mm) => { setSelected(mm); setAccepted(false); }} />)}
                        </div>

                        {selected && <TermsPanel m={selected} accepted={accepted} onAccept={setAccepted} />}

                        {selected && accepted && (
                            <div className="space-y-4" data-testid="payment-section">
                                <div className="rounded-2xl bg-[#1973B8]/10 border border-[#1973B8]/30 px-5 py-4 flex flex-wrap items-center justify-between gap-3" data-testid="selected-summary">
                                    <div>
                                        <p className="text-[10.5px] uppercase tracking-wider text-[#7CB1E5]">Importe a transferir</p>
                                        <p className="text-3xl font-bold text-white tabular-nums" data-testid="selected-amount">{fmt(selected.amount)}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[10.5px] uppercase tracking-wider text-slate-500">Modalidad seleccionada</p>
                                        <p className="text-sm text-white font-semibold" data-testid="selected-modality">{selected.title} · {selected.subtitle}</p>
                                    </div>
                                </div>

                                <AgentData agent={cfg.agent} />

                                <div className="rounded-2xl bg-slate-900/70 border border-slate-800 p-5 space-y-2" data-testid="instructions">
                                    <p className="text-white font-semibold text-sm tracking-wide">INSTRUCCIONES PARA EL USUARIO</p>
                                    <p className="text-slate-300 text-sm leading-relaxed">«{cfg.instructions}»</p>
                                </div>

                                <div className="rounded-2xl bg-slate-900/70 border border-slate-800 p-5 space-y-4">
                                    <p className="text-white font-semibold text-sm flex items-center gap-2"><UploadCloud className="w-4 h-4 text-[#7CB1E5]" /> Adjuntar justificante (PDF, JPG o PNG · máx 12 MB)</p>
                                    <input ref={fileRef} type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={(e) => pickFile(e.target.files?.[0] || null)} data-testid="wp-file-input" />
                                    {file ? (
                                        <div className="flex items-center gap-2 rounded-md bg-slate-950 border border-emerald-500/40 px-3 py-2 text-xs" data-testid="wp-file-selected">
                                            <Paperclip className="w-3.5 h-3.5 text-emerald-300" />
                                            <span className="text-white truncate flex-1">{file.name}</span>
                                            <span className="text-slate-500">{(file.size / 1024).toFixed(0)} KB</span>
                                            <button type="button" onClick={() => pickFile(null)} className="text-slate-400 hover:text-white" data-testid="wp-file-clear"><X className="w-3.5 h-3.5" /></button>
                                        </div>
                                    ) : (
                                        <button type="button" onClick={() => fileRef.current?.click()}
                                            className="w-full rounded-md border border-dashed border-slate-700 bg-slate-950 px-3 py-4 text-xs text-slate-400 hover:border-[#1973B8] hover:text-white transition-colors" data-testid="wp-file-pick">
                                            Haz clic para seleccionar el justificante
                                        </button>
                                    )}
                                    <div className="space-y-1.5">
                                        <Label className="text-slate-400 text-xs">Nota para Administración (opcional)</Label>
                                        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ej: transferencia realizada hoy desde mi banca online" className="bg-slate-950 border-slate-800 text-white text-sm" data-testid="wp-note-input" />
                                    </div>
                                    <Button onClick={submit} disabled={sending || !file} className="bg-[#1973B8] hover:bg-[#1973B8]/85 text-white" data-testid="wp-submit-btn">
                                        {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UploadCloud className="w-4 h-4 mr-2" />} Enviar justificante a Administración
                                    </Button>
                                </div>
                            </div>
                        )}
                    </>
                )}

                <div className="rounded-2xl bg-slate-900/70 border border-slate-800 overflow-hidden" data-testid="wp-tracking">
                    <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
                        <p className="text-white font-semibold text-sm">Seguimiento de mis solicitudes</p>
                        <p className="text-[11px] text-slate-500">Pendiente → En revisión → Verificado / Rechazado</p>
                    </div>
                    {requests.length === 0 ? (
                        <p className="text-slate-600 text-sm text-center py-10" data-testid="wp-empty">Aún no has enviado ningún justificante.</p>
                    ) : (
                        <div className="divide-y divide-slate-800/60">{requests.map((r) => <RequestItem key={r.id} r={r} />)}</div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default TransferToMyBankPage;
